import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCustomVoucher,
  getTrackingEvent,
  postOfferView,
  postTagEmailOffer,
  postTrackingBundle,
} from "../../../../../services/offers/offers.service";
import {
  CampaignType,
  DealLayoutSettings,
  ICONS_DEFAULT_VALUES,
  LayoutOptions,
  MainLayoutSettings,
  Offer,
  OtherSettingsInterface,
  PlacementStage,
  QueryBundle,
  QueryPostOfferView,
  QuerySaveEmail,
  RedeemBy,
  Template,
  TemplateIcons,
  VoucherType,
} from "../../../../../types/global.types";
import { EMAIL_REGEX } from "../../../../../utility/regexp";
import PreSelect from "../../PreSelect/PreSelect";
import "./style.scss";
import TemplateIcon from "../../Icons/info.component";
import offerImageSrc from "../../../../../assets/images/offer-listening.png";
import BlockedTag from "../../BlockedTag/blockedTag.component";
import ImageCarousel from "../../../../ImageCarousel";
import FeaturedOfferVideo, {
  getFeaturedOfferVideoUrl,
} from "../FeaturedOfferVideo/FeaturedOfferVideo";

interface Props {
  offer: Offer;
  slot: number;
  settings: DealLayoutSettings;
  mainSettings: MainLayoutSettings;
  settingsTemplate: Template;
  displayOnly: boolean;
  isAdded: boolean;
  retailerId: string;
  campaignId: string;
  placementId: string;
  placementName: string;
  campaign: CampaignType;
  defaultOpen: boolean;
  preEmail: string | undefined;
  sessionId: string;
  setPreEmail?: (email: string) => void;
  openSingle: (
    o: Offer,
    isSave: boolean,
    isAutoEmail: boolean,
    slot: number,
    isShare?: boolean
  ) => void;
  onAdd: (offer: Offer, value: boolean, slot: number) => void;
  hasVoucher: boolean;
  offerLength: number;
  otherSettings: OtherSettingsInterface | undefined;
  layoutId: string;
  layoutOptions: LayoutOptions | undefined;
  canAutoSendEmailFromLabel?: boolean;
  canAutoSendEmailFromMainCTA?: boolean;
  onAutoSendEmailFromLabel?: (offer: Offer, slot: number) => void;
  autoSendingOfferId?: string;
  openEmailInPopup?: boolean;
}

const FeaturedOfferSmall = ({
  offer,
  preEmail,
  setPreEmail,
  slot,
  settings,
  mainSettings,
  campaign,
  settingsTemplate,
  displayOnly,
  retailerId,
  campaignId,
  placementId,
  placementName,
  isAdded,
  openSingle,
  onAdd,
  hasVoucher,
  offerLength,
  otherSettings,
  defaultOpen,
  sessionId,
  layoutId,
  layoutOptions,
  canAutoSendEmailFromLabel,
  canAutoSendEmailFromMainCTA,
  onAutoSendEmailFromLabel,
  autoSendingOfferId,
  openEmailInPopup = false,
}: Props) => {
  const [email, setEmail] = useState<string | undefined>(preEmail);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [validEmail, setValidEmail] = useState<boolean>(
    preEmail ? true : false
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const [emailPopulate, setEmailPopulate] = useState<boolean>(true);
  const [emailSwitch, setEmailSwitch] = useState<boolean>(
    preEmail || campaign.campaignGeneralOptions.automaticallyCheckOptIn
      ? true
      : false
  );
  const [sentClick, setSentClick] = useState<boolean>(false);
  const [showManualEmailForm, setShowManualEmailForm] = useState<boolean>(false);
  const [copyCode, setCopyCode] = useState<boolean>(false);
  const [getCode, setGetCode] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [customVoucher, setCustomVoucher] = useState<string>("");
  const [voucherLoading, setVoucherLoading] = useState<boolean>(false);
  const elmRef = useRef<HTMLDivElement>(null);
  const emailSentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const EMAIL_SENT_LABEL_MS = 2000;

  const imageUrl = useMemo(() => {
    return offer.media.featured.images.length
      ? offer.media.featured.images[0].filepath
      : offer.imageUrl || offerImageSrc;
  }, [offer]);

  const title = useMemo(() => {
    return offer.media.featured.title
      ? offer.media.featured.title
      : offer.title;
  }, [offer]);

  const description = useMemo(() => {
    return offer.media.featured.description
      ? offer.media.featured.description
      : offer.description;
  }, [offer]);

  const handleAdd = (
    e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    const value =
      e.target instanceof HTMLInputElement ? e.target.checked : false;
    if (displayOnly && !offer.tagBlocked.isBlocked) {
      onAdd(offer, value, slot);
      value && handleOpenEmailInput();
    }
  };

  function observeIframeVisibility(elm: HTMLElement, callback: () => void) {
    if (!elm) {
      console.error(`Element not found.`);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback();
            // Optionally, stop observing after the first intersection
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.5, // Trigger when at least 50% of the iframe is visible
      }
    );

    observer.observe(elm);
  }

  const setDefaultOfferImpressionEvent = async () => {
    if (offer) {
      const data: QueryPostOfferView = {
        offers: [{ id: offer.id, slot }],
        campaignId,
        campaignName: campaign.name || "",
        companyId: campaign.company_Id,
        placementId,
        placementName,
        pageUrl: "",
        templateId: settingsTemplate.identifier,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId,
      };

      await postOfferView(data);
    }
  };

  useEffect(() => {
    if (elmRef.current) {
      observeIframeVisibility(elmRef.current, () => {
        setDefaultOfferImpressionEvent();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenEmailInput = () => {
    if (
      campaign?.campaignGeneralOptions.showAddToOrder &&
      campaign?.campaignGeneralOptions.emailCaptureOnOffers &&
      campaign?.campaignGeneralOptions.emailCaptureOnAddToOrder
    ) {
      openSingle(offer, false, true, slot);
    }
  };

  const handleGetGift = async (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    url: string
  ) => {
    if (offer?.voucher?.voucherType === VoucherType.CUSTOM && !getCode) {
      return;
    }

    if (
      event.target instanceof HTMLElement &&
      !event.target.classList.contains("featured-offer-info-icon") &&
      !event.target.classList.contains("copy-box") &&
      !event.target.classList.contains("non-email-link") &&
      !displayOnly &&
      offer.offerSettings.redeemBy !== RedeemBy.email
    ) {
      if (offer.offerSettings.bundleOptions.isBundled) {
        const data: QueryBundle = {
          offerId: offer.id,
          campaignId: campaign.id,
          placementId: placementId,
          orderId: offer.orderId,
          userSessionId: sessionId,
          imageUrl: imageUrl,
          pageUrl: window.location.href,
          templateId: settingsTemplate.identifier,
          placementName: placementName,
          pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
          slot: slot,
          layoutId: layoutId,
        };

        const response = await postTrackingBundle(data);

        if (response?.data?.status) {
          window.open(`/bundle-offer/${response?.data?.result}`, "_blank");
        }
      } else {
        window.open(
          url + `&tid=${settingsTemplate.identifier}&slot=${slot}`,
          "_blank"
        );
      }
    }
  };

  const handleClickInfo = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    event.preventDefault();
    openSingle(offer, false, false, slot);
    getTrackingEvent({
      event: "onreadmoreclick",
      offerId: offer.id,
      retailerId,
      campaignId,
      placementId,
      placementName,
      tid: settingsTemplate.identifier,
      slot,
      pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
      pltid: layoutId,
    });
  };

  const handleClickShare = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    event.preventDefault();
    openSingle(offer, false, false, slot, true);
  };

  const submitSaveForm = async (
    e: React.FormEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    e.preventDefault();
    setSentClick(true);
    if ((email && validEmail) || preEmail) {
      setLoading(true);
      const emailParams: QuerySaveEmail = {
        offerid: offer.id,
        email: email || preEmail || "",
        offerLink: offer.trackingUrl,
        companyId: retailerId,
        placementId,
        campaignId: campaignId,
        templateId: settingsTemplate.identifier,
        slot,
        orderId: offer.orderId,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        layoutId,
      };

      const response = await postTagEmailOffer(emailParams);
      getTrackingEvent({
        event: "onsendemailclick",
        retailerId,
        campaignId: campaignId,
        offerId: offer.id,
        placementId,
        placementName,
        tid: settingsTemplate.identifier,
        slot,
        pltype: displayOnly ? PlacementStage.pre : PlacementStage.post,
        pltid: layoutId,
      });

      if (response?.data?.status) {
        setEmailSent(true);
        setPreEmail && setPreEmail(email || preEmail || "");
        setShowManualEmailForm(false);
      }
    }

    setLoading(false);
  };

  const handleMainCtaAutoSend = () => {
    if (
      canAutoSendEmailFromMainCTA &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail &&
      !offer.offerSettings?.bundleOptions?.isBundled &&
      !autoSendingOfferId
    ) {
      onAutoSendEmailFromLabel(offer, slot);
    }
  };

  const shouldShowEmailSection =
    !displayOnly &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowSavingOffers &&
    campaign.campaignGeneralOptions.saveOfferOptions.allowEmail &&
    !offer.offerSettings?.bundleOptions?.isBundled &&
    (layoutOptions?.showEmail ?? true);

  const wasAutoSendingRef = useRef(false);

  const handleEmailLabelAction = () => {
    if (autoSendingOfferId === offer.id) {
      return;
    }

    if (
      canAutoSendEmailFromLabel &&
      onAutoSendEmailFromLabel &&
      offer.offerSettings.redeemBy === RedeemBy.webemail
    ) {
      onAutoSendEmailFromLabel(offer, slot);
      return;
    }

    if (openEmailInPopup) {
      openSingle(offer, true, false, slot);
      return;
    }

    setShowManualEmailForm(true);
  };

  const closeManualEmailForm = () => {
    setShowManualEmailForm(false);
    setSentClick(false);
  };

  const renderAutoEmailLabel = () => (
    <div
      className="super-item-email-label super-email-func non-email-link"
      onClick={handleEmailLabelAction}
      tabIndex={0}
      role="button"
      aria-label="Add to email"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleEmailLabelAction();
        }
      }}
      style={{
        backgroundColor: `${settings?.emailBackground}`,
        borderTop: `1px solid ${settings?.emailBorderColor}`,
        marginBottom: offer.voucher?.voucherType ? "20px" : "0",
      }}
    >
      <span
        className="super-item-email-icon super-email-func"
        style={{ backgroundColor: settings?.backgroundColor }}
      >
        {!settings?.icons || settings?.icons === TemplateIcons.default ? (
          <TemplateIcon
            icon={settings?.iconEmail?.value || ICONS_DEFAULT_VALUES.iconEmail}
            color={settings?.emailIconColor || "#888888"}
          />
        ) : (
          <svg
            width="32"
            height="24"
            viewBox="0 0 32 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginLeft: 5 }}
          >
            <path
              d="M21 4.00024H3C2.45 4.00024 2 4.45024 2 5.00024V19.0002C2 19.5502 2.45 20.0002 3 20.0002H21C21.55 20.0002 22 19.5502 22 19.0002V5.00024C22 4.45024 21.55 4.00024 21 4.00024ZM20.38 5.00024L12 13.0802L3.69 5.00024H20.38ZM3 19.0002V5.72024L12 14.4702L21 5.80024V19.0002H3Z"
              fill={settings?.emailIconColor || "#888888"}
            />
          </svg>
        )}
      </span>
      <span
        className="super-email-func non-email-link"
        style={{
          color: settings?.emailTextColor || "#888888",
          fontSize: `${settings?.emailFontSize}px`,
          fontWeight: settings?.emailFontWeight || "400",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {autoSendingOfferId === offer.id ? (
          "Sending email..."
        ) : emailSent ? (
          <>
            Email sent
            <svg
              width="12"
              height="9"
              viewBox="0 0 12 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginLeft: "5px" }}
              aria-hidden="true"
            >
              <path
                d="M11.5667 0.28139C11.9134 0.644571 11.9 1.22002 11.5368 1.56669L4.87009 7.93033C4.51879 8.26565 3.96597 8.26565 3.61468 7.93032L0.281383 4.7485C-0.0817948 4.40183 -0.095174 3.82638 0.2515 3.4632C0.598174 3.10002 1.17362 3.08664 1.5368 3.43332L4.24239 6.01596L10.2814 0.251494C10.6446 -0.0951766 11.22 -0.0817918 11.5667 0.28139Z"
                fill={settings?.emailTextColor || "#888888"}
              />
            </svg>
          </>
        ) : (
          settings?.emailMeTheGift || "Email me the gift"
        )}
      </span>
    </div>
  );

  const renderManualEmailForm = () => (
    <form
      className={`non-email-link ${isLoading ? "loading-overlay" : ""}`}
      style={{
        position: "relative",
        backgroundColor: `${settings?.emailBackground}`,
        borderTop: `1px solid ${settings?.emailBorderColor}`,
        marginBottom: offer.voucher?.voucherType ? "20px" : "0",
      }}
    >
      <div
        className="super-desktop-minimize-email-sm non-email-link"
        onClick={closeManualEmailForm}
        tabIndex={0}
        role="button"
        aria-label="Close email form"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            closeManualEmailForm();
          }
        }}
        style={{
          backgroundColor: settings?.backgroundColor,
        }}
      >
        <svg
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M0.176625 0.34468C0.385958 0.133389 0.710902 0.109912 0.945926 0.274249L1.02944 0.34468L7.41698 6.79213L13.9706 0.178277C14.2061 -0.0594256 14.5879 -0.0594256 14.8234 0.178277C15.0327 0.389568 15.056 0.717552 14.8932 0.954775L14.8234 1.03907L8.2698 7.65293L14.5195 13.9609C14.755 14.1986 14.755 14.584 14.5195 14.8217C14.3102 15.033 13.9852 15.0565 13.7502 14.8922L13.6667 14.8217L7.41698 8.51372L1.17826 14.8118C0.942765 15.0495 0.560944 15.0495 0.325445 14.8118C0.116112 14.6005 0.0928526 14.2726 0.255667 14.0353L0.325445 13.951L6.56416 7.65293L0.176625 1.20548C-0.0588749 0.967774 -0.0588749 0.582382 0.176625 0.34468Z"
            fill={settings?.textColor || "#000"}
          />
        </svg>
      </div>
      <div
        className="featured-offer-email non-email-link"
        style={{
          backgroundColor: settings?.emailDropdownBackground || "#ffffff",
        }}
      >
        {isLoading && (
          <div className="icon-spin">
            <svg
              className="spin"
              viewBox="0 0 1024 1024"
              focusable="false"
              data-icon="loading"
              width="1em"
              height="1em"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
            </svg>
          </div>
        )}
        <div
          className="featured-offer-email-wrapper non-email-link"
        >
          <div
            className="featured-offer-email-title non-email-link"
            style={{ color: settings?.emailTextColor || "#888888" }}
          >
            {settings?.emailMeTheGift || "Email me the gift"}
          </div>
          <div
            className="featured-offer-email-terms non-email-link"
            style={{
              color: settings?.emailTextColor || "#888888",
            }}
          >
            Your data will be processed according to BrandSwap's
            <a
              href="https://brandswap.com/privacy/"
              rel="noopener noreferrer"
              target="_blank"
              className="non-email-link"
              style={{ marginLeft: 5 }}
            >
              {" "}
              Privacy Policy
            </a>
          </div>
          <div className="featured-offer-email-terms non-email-link">
            <label
              htmlFor={`accept-email-${offer.id}`}
              className="non-email-link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setEmailSwitch(!emailSwitch);
                }
              }}
              style={{
                color: settings?.emailTextColor || "#888888",
                cursor: "pointer",
              }}
            >
              <input
                id={`accept-email-${offer.id}`}
                type="checkbox"
                checked={emailSwitch}
                onChange={(e) => setEmailSwitch(e.target.checked)}
                className="checkbox-custom non-email-link"
              />
              <div className="checkboxsvg non-email-link"></div>I agree to share
              my email address with BrandSwap to receive details about this offer
            </label>
          </div>
        </div>

        <div className="featured-offer-email-input-wrapper">
          <div className="featured-offer-email-input non-email-link">
            <input
              type="text"
              className="non-email-link"
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailSent(false);
                setEmailPopulate(false);
              }}
              value={emailPopulate ? preEmail || email || "" : email || ""}
              placeholder={settings?.enterEmail || "Enter E-mail"}
              style={{
                borderRadius: `${settings?.emailButtonRounding}px`,
              }}
              tabIndex={0}
              role="button"
              aria-label="Save offer to email"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  submitSaveForm(e);
                }
              }}
            />
            <button
              className="button non-email-link"
              style={{
                color: settings?.emailButtonTextColor || "#ffffff",
                backgroundColor: settings?.emailButtonBackgroundColor || "#888888",
                borderRadius: `${settings?.emailButtonRounding}px`,
                borderColor:
                  settings?.emailButtonBorderColor ||
                  settings?.emailButtonBackgroundColor ||
                  "#888888",
              }}
              onClick={submitSaveForm}
              disabled={!emailSwitch || !validEmail}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  submitSaveForm(e);
                }
              }}
            >
              {settings?.emailSend || "Send"}
              {emailSent && !isLoading && (
                <svg
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    marginLeft: "5px",
                  }}
                >
                  <path
                    d="M11.5667 0.28139C11.9134 0.644571 11.9 1.22002 11.5368 1.56669L4.87009 7.93033C4.51879 8.26565 3.96597 8.26565 3.61468 7.93032L0.281383 4.7485C-0.0817948 4.40183 -0.095174 3.82638 0.2515 3.4632C0.598174 3.10002 1.17362 3.08664 1.5368 3.43332L4.24239 6.01596L10.2814 0.251494C10.6446 -0.0951766 11.22 -0.0817918 11.5667 0.28139Z"
                    fill={settings?.emailButtonTextColor || "#ffffff"}
                  />
                </svg>
              )}
            </button>

            {emailSent && offer.offerSettings.redeemBy === RedeemBy.webemail && (
              <div className="label-wrapper">
                <span className="label-success"> Email sent</span>
              </div>
            )}
          </div>
          {!validEmail && email && sentClick && (
            <div className="label-wrapper">
              <span className="label-error">Must be valid email!</span>
            </div>
          )}
        </div>
      </div>
    </form>
  );

  const handleCopyHtml = () => {
    window.focus();
    navigator.clipboard?.writeText(
      customVoucher ? customVoucher : offer.voucher?.code
    );
    setCopied(true);
    setCopyCode(false);
    setTimeout(() => {
      setCopied(false);
    }, 1000); // Show the message for 1 second
  };

  const handleGetCode = async () => {
    setVoucherLoading(true);
    const customVoucher = await getCustomVoucher({
      offerId: offer.id,
      campaignId: campaign.id,
      campaignName: campaign.name,
      companyId: campaign.company_Id,
      placementId: placementId,
      placementName: placementName,
      templateId: settingsTemplate.identifier,
      layoutId: layoutId,
      orderId: offer.orderId,
    });
    setCustomVoucher(customVoucher?.data?.result);
    setGetCode(true);
    setVoucherLoading(false);
  };

  const getVoucherText = () => {
    return customVoucher ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>

        <span style={{ fontWeight: "600" }}>{customVoucher}</span>
      </>
    ) : offer.voucher?.code ? (
      <>
        <span style={{ fontWeight: "400", marginRight: "3px" }}>Code: </span>
        <span style={{ fontWeight: "600" }}>{offer.voucher?.code}</span>
      </>
    ) : (
      <span style={{ fontWeight: "400" }}>Sorry, code just expired!</span>
    );
  };

  // Validate email on change
  useEffect(() => {
    setSentClick(false);
    if (email) {
      const isValid = EMAIL_REGEX.test(email);
      setValidEmail(isValid);
    }
    // eslint-disable-next-line
  }, [email, preEmail]);

  useEffect(() => {
    setEmail(preEmail);
    // eslint-disable-next-line
  }, [preEmail]);

  useEffect(() => {
    if (canAutoSendEmailFromLabel) {
      setShowManualEmailForm(false);
    }
  }, [canAutoSendEmailFromLabel]);

  useEffect(() => {
    if (autoSendingOfferId === offer.id) {
      wasAutoSendingRef.current = true;
      return;
    }

    if (wasAutoSendingRef.current && canAutoSendEmailFromLabel) {
      wasAutoSendingRef.current = false;
      setEmailSent(true);
    }
  }, [autoSendingOfferId, offer.id, canAutoSendEmailFromLabel]);

  useEffect(() => {
    if (!emailSent) {
      return;
    }

    if (emailSentTimeoutRef.current) {
      clearTimeout(emailSentTimeoutRef.current);
    }

    emailSentTimeoutRef.current = setTimeout(() => {
      setEmailSent(false);
    }, EMAIL_SENT_LABEL_MS);

    return () => {
      if (emailSentTimeoutRef.current) {
        clearTimeout(emailSentTimeoutRef.current);
      }
    };
  }, [emailSent]);

  return (
    <>
      <div
        className={`featured-offer-small ${displayOnly && campaign?.campaignGeneralOptions.showAddToOrder
          ? "display"
          : "getbutton"
          }`}
        ref={elmRef}
      >
        <input
          className="hidden-input"
          id={offer.id}
          type="checkbox"
          disabled={
            !displayOnly || !campaign?.campaignGeneralOptions.showAddToOrder
          }
          checked={isAdded}
          onChange={handleAdd}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleAdd(e);
            }
          }}
        />
        {(layoutOptions?.showBadge ?? true) && (
          <span
            style={{
              position: "absolute",
              top: "-8.5px",
              left: "20px",
              fontSize: "10px",
              textTransform: "uppercase",
              color: offer.badge?.fontColor,
              backgroundColor: offer.badge?.backgroundColor,
              padding: "0 6px",
              borderRadius: "3px",
              fontWeight: "600",
              zIndex: 10,
            }}
            className="badge-settings"
          >
            {offer.badge?.name}
          </span>
        )}
        <label
          htmlFor={offer.id}
          className="featured-offer-layout"
          onClick={(e) => handleGetGift(e, offer.trackingUrl)}
          style={{
            backgroundColor: settings?.backgroundColor,
            fontSize: settings?.fontSize,
            color: settings?.textColor,
            border: `1px solid ${isAdded
              ? mainSettings?.highlightColor || settings?.borderColor || "#3ab78f"
              : offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
                ? offer.badge?.backgroundColor
                : settings?.borderColor || "#d6dae5"
              }`,
            borderBottomLeftRadius: displayOnly
              ? `${typeof mainSettings?.borderRadius === "number"
                ? mainSettings?.borderRadius
                : 10
              }px`
              : "0",
            borderBottomRightRadius: displayOnly
              ? `${typeof mainSettings?.borderRadius === "number"
                ? mainSettings?.borderRadius
                : 10
              }px`
              : "0",
            borderRadius: `${typeof mainSettings?.borderRadius === "number"
              ? mainSettings?.borderRadius
              : 10
              }px`,
            position: "relative",
          }}
        >
          {offer.tagBlocked.isBlocked && (
            <BlockedTag tagBlocked={offer.tagBlocked} />
          )}
          <div className="featured-offer-info">
            <div className="featured-offer-image">
              <FeaturedOfferVideo
                showVideo={!!mainSettings?.showFeaturedVideo}
                videoUrl={getFeaturedOfferVideoUrl(offer)}
                objectFit="contain"
              >
                {offer.media.featured.images.length > 0 ? (
                  <ImageCarousel
                    images={offer.media.featured.images.map(
                      (image) => image.filepath
                    )}
                    showPagination={true}
                    altPrefix="feature"
                    style={{ width: "100%", height: "auto" }}
                  />
                ) : (
                  <img
                    className={`image-large`}
                    src={imageUrl}
                    alt=""
                    role="presentation"
                  />
                )}
              </FeaturedOfferVideo>
            </div>

            <div
              className={`featured-offer-content`}
              style={{
                paddingBottom:
                  hasVoucher &&
                    !(
                      !displayOnly &&
                      campaign.campaignGeneralOptions.saveOfferOptions
                        .allowSavingOffers &&
                      campaign.campaignGeneralOptions.saveOfferOptions
                        .allowEmail &&
                      !offer.offerSettings?.bundleOptions?.isBundled &&
                      (layoutOptions?.showEmail ?? true)
                    )
                    ? "30px"
                    : "15px",
              }}
            >
              <div className="featured-offer-content-info">
                <div className="featured-offer-title">
                  <span
                    className={`featured-offer-title-text max-2lines `}
                    style={{
                      color: settings?.textColor,
                      fontSize: `${Number(settings?.fontSize) + 1}px`,
                    }}
                    role="contentinfo"
                    tabIndex={0}
                    aria-label={title}
                  >
                    {title}
                  </span>
                </div>
                <div
                  className="featured-offer-description"
                  style={{
                    color: settings?.textColor,
                    fontSize: `${settings?.fontSize}px`,
                  }}
                  role="contentinfo"
                  tabIndex={0}
                  aria-label={description}
                >
                  {description}
                </div>
              </div>

              <div className="featured-offer-get featured-offer-get-parts">
                <div className="featured-offer-get-center">
                  {displayOnly ? (
                    <>
                      {campaign?.campaignGeneralOptions.showAddToOrder && (
                        <PreSelect
                          offerId={offer.id}
                          buttonFontWeight={settings?.buttonFontWeight}
                          buttonFontSize={settings?.buttonFontSize}
                          showCheckboxSelect={otherSettings?.showCheckboxSelect}
                          checked={isAdded}
                          highlightColor={mainSettings?.highlightColor}
                          borderColor={settings?.borderColor}
                          selectedButtonText={otherSettings?.selectedButtonText}
                          notSelectedButtonText={
                            otherSettings?.notSelectedButtonText
                          }
                          settings={otherSettings}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {offer?.voucher?.voucherType === VoucherType.CUSTOM &&
                        !getCode ? (
                        <div
                          className="dashed-button-super copy-box"
                          onClick={handleGetCode}
                          style={{
                            fontSize: `${settings?.buttonFontSize}px`,
                            position: "relative",
                            borderRadius: `${mainSettings?.borderRadius}px`,
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label="Get code"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleGetCode();
                            }
                          }}
                        >
                          {voucherLoading ? (
                            <svg
                              className="spin"
                              viewBox="0 0 1024 1024"
                              focusable="false"
                              data-icon="loading"
                              width="1em"
                              height="1em"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
                            </svg>
                          ) : (
                            "Get code"
                          )}
                        </div>
                      ) : (
                        offer.offerSettings.redeemBy === RedeemBy.webemail && (
                          <div
                            className="featured-offer-action-get"
                            style={{
                              color: settings?.buttonTextColor,
                              fontSize: `${settings?.buttonFontSize}px`,
                              fontWeight: settings?.buttonFontWeight,
                              backgroundColor: settings?.buttonBackgroundColor,
                              borderRadius: settings?.buttonRounding,
                              border: `1px solid ${settings?.buttonBorderColor ||
                                settings?.buttonBackgroundColor
                                }`,
                              textTransform: settings?.buttonTextTransform,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleGetGift(e, offer.trackingUrl);
                              handleMainCtaAutoSend();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                                handleGetGift(e, offer.trackingUrl);
                                handleMainCtaAutoSend();
                              }
                            }}
                          >
                            {offer?.offerCTAText || "Get offer now"}
                          </div>
                        )
                      )}
                    </>
                  )}
                </div>

                <div className="featured-offer-get-icons">
                  <div
                    className={`featured-offer-info-icon ${!displayOnly &&
                      campaign.sharingOptions?.allowSharing &&
                      campaign.sharingOptions.sharingPlatforms?.platforms
                        ?.length > 0 &&
                      offer?.offerSettings?.allowShare &&
                      (layoutOptions?.showShare ?? true)
                      ? "featured-offer-info-icon--mbot10"
                      : ""
                      }`}
                    onClick={handleClickInfo}
                    tabIndex={0}
                    role="button"
                    aria-label="More information about offer"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        handleClickInfo(e);
                      }
                    }}
                  >
                    {!settings?.icons ||
                      settings?.icons === TemplateIcons.default ? (
                      <TemplateIcon
                        icon={
                          settings?.iconInfo?.value ||
                          ICONS_DEFAULT_VALUES.iconInfo
                        }
                        color={settings?.textColor || "#000"}
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM13.5 18.5C13.5 19.33 12.83 20 12 20C11.17 20 10.5 19.33 10.5 18.5V10.5C10.5 9.67 11.17 9 12 9C12.83 9 13.5 9.67 13.5 10.5V18.5ZM12 7C11.17 7 10.5 6.33 10.5 5.5C10.5 4.67 11.17 4 12 4C12.83 4 13.5 4.67 13.5 5.5C13.5 6.33 12.83 7 12 7Z"
                          fill={settings?.textColor}
                        />
                      </svg>
                    )}
                  </div>

                  {!displayOnly &&
                    campaign.sharingOptions?.allowSharing &&
                    campaign.sharingOptions.sharingPlatforms?.platforms
                      ?.length > 0 &&
                    offer?.offerSettings?.allowShare &&
                    (layoutOptions?.showShare ?? true) && (
                      <div
                        className="featured-offer-share-icon featured-offer-info-icon"
                        onClick={handleClickShare}
                        tabIndex={0}
                        role="button"
                        aria-label="Share offer"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.stopPropagation();
                            handleClickShare(e);
                          }
                        }}
                      >
                        <TemplateIcon
                          icon={
                            settings?.iconShare?.value ||
                            ICONS_DEFAULT_VALUES.iconShare
                          }
                          color={settings?.textColor || "#000"}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
          {shouldShowEmailSection &&
            (showManualEmailForm && !canAutoSendEmailFromLabel && !openEmailInPopup
              ? renderManualEmailForm()
              : renderAutoEmailLabel())}
          {offer.voucher?.voucherType && (
            <span
              style={{
                position: "absolute",
                bottom: "0",
                left: "0",
                fontSize: "11px",
                color: settings?.buttonTextColor,
                backgroundColor: settings?.buttonBackgroundColor,
                fontWeight: "600",
                textAlign: "center",
                width: "100%",
                height: "20px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border:
                  offer.badge?.useBorder && (layoutOptions?.showBadge ?? true)
                    ? `1px solid ${offer.badge?.backgroundColor}`
                    : "",
                borderTop: "none",
                borderBottomLeftRadius: displayOnly
                  ? `${typeof mainSettings?.borderRadius === "number"
                    ? mainSettings?.borderRadius
                    : 10
                  }px`
                  : "10px",
                borderBottomRightRadius: displayOnly
                  ? `${typeof mainSettings?.borderRadius === "number"
                    ? mainSettings?.borderRadius
                    : 10
                  }px`
                  : "10px",
              }}
              onMouseEnter={() => setCopyCode(true)}
              onMouseLeave={() => {
                setCopyCode(false);
              }}
              onClick={() => handleCopyHtml()}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  handleCopyHtml();
                }
              }}
            >
              {offer.voucher?.voucherType === VoucherType.CUSTOM && !getCode ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  className="copy-box"
                >
                  Code:
                  <svg
                    style={{
                      marginLeft: "5px",
                    }}
                    width="70"
                    height="16"
                    viewBox="0 0 70 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="70" height="16" fill="" />
                    <circle
                      cx="4"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="10"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="52"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="58"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="16"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="22"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="28"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="34"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="40"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                    <circle
                      cx="46"
                      cy="8"
                      r="2"
                      fill={settings?.buttonTextColor || "white"}
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {copyCode ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        fontWeight: "400",
                        cursor: "pointer",
                      }}
                      onClick={handleCopyHtml}
                      className="copy-box"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          handleCopyHtml();
                        }
                      }}
                    >
                      {copied ? (
                        <>
                          <svg
                            width="8"
                            height="6"
                            viewBox="0 0 8 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M1 3L3 5L7 1"
                              stroke={settings?.buttonTextColor || "white"}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copied successfully
                        </>
                      ) : (
                        <>
                          <svg
                            width="11"
                            height="10"
                            viewBox="0 0 11 10"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            <path
                              d="M7.75 3V2C7.75 1.73478 7.63147 1.48043 7.42049 1.29289C7.20952 1.10536 6.92337 1 6.625 1H2.125C1.82663 1 1.54048 1.10536 1.3295 1.29289C1.11853 1.48043 1 1.73478 1 2V6C1 6.26522 1.11853 6.51957 1.3295 6.70711C1.54048 6.89464 1.82663 7 2.125 7H3.25M4.375 3H8.875C9.49632 3 10 3.44772 10 4V8C10 8.55228 9.49632 9 8.875 9H4.375C3.75368 9 3.25 8.55228 3.25 8V4C3.25 3.44772 3.75368 3 4.375 3Z"
                              stroke={settings?.buttonTextColor || "white"}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Copy Code
                        </>
                      )}
                    </div>
                  ) : copied ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                      className="copy-box"
                    >
                      <svg
                        width="8"
                        height="6"
                        viewBox="0 0 8 6"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: "5px",
                        }}
                      >
                        <path
                          d="M1 3L3 5L7 1"
                          stroke={settings?.buttonTextColor || "white"}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied successfully
                    </div>
                  ) : (
                    getVoucherText()
                  )}
                </>
              )}
            </span>
          )}
        </label>
      </div>
    </>
  );
};

export default FeaturedOfferSmall;
